/* Stating a company's financials (ADR-0069, card C-F). Tokens only: stylelint refuses a colour
   literal, and a screen that restyled the theme would be a second theme. */

.form {
  display: grid;
  gap: 12px;
}

.fieldRow {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.field {
  display: grid;
  gap: 4px;
  min-width: 0;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
}

.field input,
.field select {
  padding: 6px 8px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 400;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
}

/* A refused field is marked by the attribute a screen reader reads; the colour is the second
   signal and never the only one. */
.field input[aria-invalid="true"],
.field select[aria-invalid="true"] {
  border-color: var(--crit);
}

.hint {
  font-size: var(--t-min);
  font-weight: 400;
  line-height: 1.45;
  color: var(--muted);
}

.fieldError {
  font-size: var(--t-min);
  font-weight: 500;
  line-height: 1.45;
  color: var(--crit);
}

/* The five questions. A group of radios is a fieldset; neither is chosen until a person chooses. */
.facts {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid var(--line-2);
  border-radius: 6px;
}

.facts legend {
  padding: 0 4px;
  font-size: var(--t-min);
  font-weight: 650;
  color: var(--ink-2);
}

.question {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 14px;
  margin: 0;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: var(--t-dense);
  color: var(--ink);
}

.question legend {
  float: left;
  padding: 0;
}

.question[aria-invalid="true"] {
  border-color: var(--crit-line);
  background: var(--crit-w);
}

.choices {
  display: flex;
  gap: 14px;
}

.choices label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: var(--t-dense);
  color: var(--ink);
}

.group {
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  border: 0;
}

.group > summary,
.groupTitle {
  font-size: var(--t-min);
  font-weight: 650;
  color: var(--ink-2);
}

.refused {
  margin: 0;
  padding: 7px 10px;
  border: 1px solid var(--crit-line);
  border-left-width: 3px;
  border-radius: 6px;
  background: var(--crit-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--crit);
}

.refused ul {
  margin: 4px 0 0;
  padding-left: 18px;
}

.result {
  display: grid;
  gap: 8px;
}

.positions {
  list-style: none;
  margin: 0;
  padding: 0;
}

.position {
  display: grid;
  gap: 3px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
  font-size: var(--t-dense);
  color: var(--ink);
}

.position:last-child {
  border-bottom: 0;
}

.line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}

.name {
  font-weight: 650;
}

.sub {
  font-size: var(--t-min);
  color: var(--muted);
}

.pill {
  display: inline-block;
  padding: 1px 7px;
  border: 1px solid var(--line-2);
  border-radius: 999px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
}

.pill::first-letter {
  text-transform: uppercase;
}

.pillRequired {
  border-color: var(--accent-line);
  background: var(--accent-w);
  color: var(--accent);
}

/* "Needs figures" is a warning about our data, not a verdict on the company. */
.pillUnknown {
  border-color: var(--warn-line);
  background: var(--warn-w);
  color: var(--warn);
}

.flag {
  padding: 1px 7px;
  border: 1px solid var(--warn-line);
  border-radius: 6px;
  background: var(--warn-w);
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--warn);
}

.needs {
  font-size: var(--t-min);
  line-height: 1.45;
  color: var(--warn);
}

.cite {
  font-size: var(--t-min);
  line-height: 1.45;
  color: var(--muted);
}

/* The rules behind a position, folded: the answer first, the reasons on request. */
.why {
  font-size: var(--t-min);
  color: var(--ink-2);
}

.why > summary {
  cursor: pointer;
  color: var(--muted);
}

.whyList {
  list-style: none;
  display: grid;
  gap: 6px;
  margin: 6px 0 0;
  padding: 0;
}

.whyRow {
  display: grid;
  grid-template-columns: 14px 1fr;
  gap: 6px;
  line-height: 1.45;
}

.whyDispute {
  display: block;
  margin-top: 2px;
  color: var(--warn);
}

/* The way on to the filing's own checklist: a link, named for what is behind it. */
.open {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 24px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--accent);
  text-decoration: none;
}

.open:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
}
